import React, { useState, useEffect } from 'react';

export interface PlacementCommitments {
  general_shelf: boolean;
  fridge_placement: boolean;
  fridge_facings_count: number;
  dry_shelf_placement: boolean;
  dry_shelf_facings_count: number;
  poster_placement: boolean;
  display_stand: boolean;
  checkout_placement: boolean;
}

interface PlacementCommitmentsFormProps {
  value?: PlacementCommitments;
  onChange: (value: PlacementCommitments) => void;
}

const defaultPlacements: PlacementCommitments = {
  general_shelf: false,
  fridge_placement: false,
  fridge_facings_count: 0,
  dry_shelf_placement: false,
  dry_shelf_facings_count: 0,
  poster_placement: false,
  display_stand: false,
  checkout_placement: false,
};

export const PlacementCommitmentsForm: React.FC<PlacementCommitmentsFormProps> = ({
  value,
  onChange,
}) => {
  const [placements, setPlacements] = useState<PlacementCommitments>(
    value || defaultPlacements
  );

  useEffect(() => {
    setPlacements(value || defaultPlacements);
  }, [value]);

  useEffect(() => {
    onChange(placements);
  }, [placements]);

  const toggle = (field: keyof PlacementCommitments) => {
    setPlacements((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const updateCount = (field: keyof PlacementCommitments, val: string) => {
    const num = parseInt(val) || 0;
    setPlacements((prev) => ({ ...prev, [field]: num }));
  };

  return (
    <div className="space-y-4">
      <label className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
        <input
          type="checkbox"
          checked={placements.general_shelf}
          onChange={() => toggle('general_shelf')}
          className="h-5 w-5 text-brand-600 rounded border-gray-300"
        />
        <span className="text-gray-700 font-medium">General Shelf</span>
      </label>

      <div className="border rounded-lg p-3 space-y-3">
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={placements.fridge_placement}
            onChange={() => toggle('fridge_placement')}
            className="h-5 w-5 text-brand-600 rounded border-gray-300"
          />
          <span className="text-gray-700 font-medium">Fridge Placement</span>
        </label>
        {placements.fridge_placement && (
          <div className="pl-8">
            <label className="text-sm text-gray-600 block mb-1">Facings</label>
            <input
              type="number"
              value={placements.fridge_facings_count}
              onChange={(e) => updateCount('fridge_facings_count', e.target.value)}
              className="w-24 rounded-md border-gray-300 shadow-sm border p-2"
              min="0"
            />
          </div>
        )}
      </div>

      <div className="border rounded-lg p-3 space-y-3">
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={placements.dry_shelf_placement}
            onChange={() => toggle('dry_shelf_placement')}
            className="h-5 w-5 text-brand-600 rounded border-gray-300"
          />
          <span className="text-gray-700 font-medium">Dry Shelf</span>
        </label>
        {placements.dry_shelf_placement && (
          <div className="pl-8">
            <label className="text-sm text-gray-600 block mb-1">Facings</label>
            <input
              type="number"
              value={placements.dry_shelf_facings_count}
              onChange={(e) => updateCount('dry_shelf_facings_count', e.target.value)}
              className="w-24 rounded-md border-gray-300 shadow-sm border p-2"
              min="0"
            />
          </div>
        )}
      </div>

      <label className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
        <input
          type="checkbox"
          checked={placements.poster_placement}
          onChange={() => toggle('poster_placement')}
          className="h-5 w-5 text-brand-600 rounded border-gray-300"
        />
        <span className="text-gray-700 font-medium">Poster / Ad</span>
      </label>

      <label className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
        <input
          type="checkbox"
          checked={placements.display_stand}
          onChange={() => toggle('display_stand')}
          className="h-5 w-5 text-brand-600 rounded border-gray-300"
        />
        <span className="text-gray-700 font-medium">Display Stand</span>
      </label>

      <label className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
        <input
          type="checkbox"
          checked={placements.checkout_placement}
          onChange={() => toggle('checkout_placement')}
          className="h-5 w-5 text-brand-600 rounded border-gray-300"
        />
        <span className="text-gray-700 font-medium">Checkout Counter</span>
      </label>
    </div>
  );
};
